import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronLeft, Loader2 } from "lucide-react";
import { buildLemburFormData, submitLemburForm, getLemburList } from "../../controller/mobile/Lemburapi";
import { getSeedRecords } from "../../controller/mobile/mockapi";
import ConfirmModal from "../../components/ConfirmModal";
import type { LemburPayload } from "../../controller/mobile/Lemburtypes";

interface Props {
  idKaryawan: number;
  onCancel?: () => void;
  onSuccess?: () => void;
}

type SubmitState = "idle" | "loading" | "success" | "error";

export default function LemburFormPage({ idKaryawan, onCancel, onSuccess }: Props): React.ReactElement {
  const [namaKegiatan, setNamaKegiatan] = useState("");
  const [tanggal, setTanggal] = useState<string>(new Date().toISOString().slice(0, 10));
  const [mulai, setMulai] = useState<string>("19:44:00".slice(0,5));
  const [sampai, setSampai] = useState<string>("19:44:00".slice(0,5));
  const [keterangan, setKeterangan] = useState("");
  

  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<LemburPayload | null>(null);
  const [pendingIdAbsensi, setPendingIdAbsensi] = useState<string | null>(null);
  const [pendingIsDuplicate, setPendingIsDuplicate] = useState(false);

  const timeRangeInvalid = sampai < mulai;
  const canSubmit = namaKegiatan.length > 0 && tanggal.length > 0 && !timeRangeInvalid && submitState !== "loading";

  

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSubmit) return;

    setSubmitState("loading");
    setErrorMessage(null);

    const payload: LemburPayload = {
      idKaryawan,
      namaKegiatan,
      tanggal,
      mulai: mulai + ":00",
      sampai: sampai + ":00",
      keterangan,
    };

    const fd = buildLemburFormData(payload);
    let statusCanLembur = false;

    // if idKaryawan present, try to find today's absensi and include its id
    try {
      const recsResp: any = await getSeedRecords(String(idKaryawan));
      const records = Array.isArray(recsResp) ? recsResp : (recsResp && recsResp.data) ? recsResp.data : [];
      const today = new Date().toISOString().slice(0, 10);
      const found = (records || []).find((r: any) => {
        if (!r || !r.tanggal) return false;
        const d = String(r.tanggal).slice(0, 10);
        return d === today;
      });
      if (found && (found.id !== undefined && found.id !== null)) {
        // some APIs use different field names for status; support common variants
        const statusVal = (found.status ?? found.status_absensi ?? found.penyetujuan_aktivitas ?? "").toString().toLowerCase();
        if (statusVal !== "izin" && statusVal !== "alfa") {
          statusCanLembur = true;
          // check if there is already a lembur for this date
          try {
            const lemburResp = await getLemburList(String(idKaryawan));
            const existing = (lemburResp || []).find((l: any) => {
              if (!l || !l.tanggal) return false;
              return String(l.tanggal).slice(0, 10) === payload.tanggal;
            });
            setPendingPayload(payload);
            setPendingIdAbsensi(String(found.id));
            setPendingIsDuplicate(!!existing);
          } catch (err) {
            setPendingPayload(payload);
            setPendingIdAbsensi(String(found.id));
            setPendingIsDuplicate(false);
          }
        }
      }
    } catch (err) {
      // ignore — submit without id_absensi
      console.warn("Gagal ambil absensi hari ini:", err);
    }

    if(!statusCanLembur) {
      setSubmitState("error");
      setErrorMessage("Tidak dapat mengajukan lembur karena status absensi hari ini tidak memenuhi syarat.");
      return;
    }

    // show in-app modal confirmation (will show duplicate message if applicable)
    setShowConfirm(true);
    // keep submitState idle while waiting
    setSubmitState("idle");
    return;
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-3 rounded-b-3xl bg-blue-950 px-4 py-5 text-white">
        <button
          type="button"
          onClick={() => onCancel && onCancel()}
          className="rounded-full p-1.5 hover:bg-white/10"
          aria-label="Kembali"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-blue-200">Kerja Lembur</p>
          <h1 className="text-lg font-semibold">Ajukan Lembur</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-5">
        {errorMessage && (
          <div className="mb-4 flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{errorMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">Nama Kegiatan</label>
            <input
              value={namaKegiatan}
              onChange={(e) => setNamaKegiatan(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 focus:border-blue-950 focus:outline-none"
              placeholder="Nama Kegiatan"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Tanggal</label>
              <input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-800 focus:border-blue-950 focus:outline-none" />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Mulai</label>
              <input type="time" value={mulai} onChange={(e) => setMulai(e.target.value)} className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-800 focus:border-blue-950 focus:outline-none" />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Sampai</label>
              <input type="time" value={sampai} onChange={(e) => setSampai(e.target.value)} className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-800 focus:border-blue-950 focus:outline-none" />
            </div>
          </div>

          {timeRangeInvalid && (
            <p className="-mt-2 text-xs text-rose-600">Waktu selesai tidak boleh sebelum waktu mulai.</p>
          )}

          {/* attachment removed per request */}

          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">Keterangan</label>
            <textarea value={keterangan} onChange={(e) => setKeterangan(e.target.value)} rows={3} placeholder="Tulis keterangan..." className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-950 focus:outline-none" />
          </div>
        </form>
      </main>

      <footer className="shrink-0 border-t border-slate-200 bg-white px-5 py-4">
        <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-950 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-blue-900 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400">
          {submitState === "loading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Mengirim...
            </>
          ) : submitState === "success" ? (
            <>
              <CheckCircle2 className="h-4 w-4" />
              Terkirim
            </>
          ) : (
            "Kirim"
          )}
        </button>
      </footer>

      {showConfirm && pendingPayload && (
        <ConfirmModal
          title={pendingIsDuplicate ? "Konfirmasi Pengajuan Ulang" : "Konfirmasi Pengajuan"}
          confirmLabel={pendingIsDuplicate ? "Ajukan Ulang" : "Ajukan"}
          cancelLabel="Batal"
          onCancel={() => { setShowConfirm(false); setPendingPayload(null); setPendingIdAbsensi(null); setPendingIsDuplicate(false); }}
          onConfirm={async () => {
            setShowConfirm(false);
            setSubmitState("loading");
            const fd = buildLemburFormData(pendingPayload);
            if (pendingIdAbsensi) fd.append("id_absensi", pendingIdAbsensi);
            const res = await submitLemburForm(fd);
            setSubmitState("idle");
            setPendingPayload(null);
            setPendingIdAbsensi(null);
            setPendingIsDuplicate(false);
            if (res && res.status === 200) {
              setSubmitState("success");
              onSuccess && onSuccess();
            } else {
              setSubmitState("error");
              setErrorMessage(String(res?.message ?? "Gagal mengirim lembur"));
            }
          }}
        >
          <p>Ajukan lembur pada <strong>{new Date(pendingPayload.tanggal).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong> mulai <strong>{pendingPayload.mulai.slice(0,5)}</strong> sampai <strong>{pendingPayload.sampai.slice(0,5)}</strong>?</p>
          {pendingIsDuplicate && <p className="mt-2 text-sm text-slate-500">Sudah terdapat pengajuan lembur pada tanggal ini. Anda yakin ingin mengajukan ulang?</p>}
        </ConfirmModal>
      )}
    </div>
  );
}
